import { useCallback, useEffect, useState } from 'react';
import './CityPicker.css';
import { getEnabledLocations } from '../../services/locationService';
import { getActiveJourneys } from '../../services/journeyService';
import { getCurrentPosition, reverseGeocode } from '../../services/geoService';

function PinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 22s7-6.4 7-12a7 7 0 0 0-14 0c0 5.6 7 12 7 12Z" stroke="#6c4ce8" strokeWidth="1.9" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.4" stroke="#6c4ce8" strokeWidth="1.9" />
    </svg>
  );
}

function LocateIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3.2" stroke="#6c4ce8" strokeWidth="1.9" />
      <circle cx="12" cy="12" r="7.5" stroke="#6c4ce8" strokeWidth="1.9" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="#6c4ce8" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function normalize(name) {
  return String(name || '').trim().toLowerCase();
}

// Finds the enabled location whose city matches any of the place names
// Nominatim returned for the user's position (city, town, district, ...).
function findLocationForAddress(locations, address) {
  if (!address) return null;
  const names = new Set(
    [
      address.city,
      address.town,
      address.village,
      address.municipality,
      address.city_district,
      address.state_district,
      address.county,
    ]
      .map(normalize)
      .filter(Boolean)
  );
  return locations.find((location) => names.has(normalize(location.city))) || null;
}

// Shown once, right after login, only for a user with no city stored yet —
// the choice is then remembered (App.js persists it via
// storeSelectedLocationId), so this never reappears for a returning user.
export default function CityPicker({ onSelectLocation }) {
  // null while the Laravel request is in flight; an array (possibly empty)
  // once it resolves.
  const [locations, setLocations] = useState(null);
  // Live-location lookup state:
  // { status: 'locating' | 'denied' | 'error' | 'done', address?, placeName? }
  const [geo, setGeo] = useState({ status: 'locating' });

  useEffect(() => {
    let cancelled = false;

    // Only a location with a live (active) journey is worth showing here —
    // picking one with none would just land the user on ShopMap's "No shops
    // available yet" empty state. Admin's own exclusivity rule (at most one
    // active journey per location) means matching on location_id here can
    // never pick up more than one journey per location.
    Promise.all([getEnabledLocations(), getActiveJourneys()])
      .then(([enabledLocations, activeJourneys]) => {
        if (cancelled) return;
        const liveLocationIds = new Set(activeJourneys.map((journey) => journey.location_id));
        setLocations(enabledLocations.filter((location) => liveLocationIds.has(location.id)));
      })
      .catch((err) => {
        console.error('CityPicker: failed to load locations from Laravel.', err);
        if (!cancelled) setLocations([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Asks for location permission (the browser shows its prompt on the first
  // call) and reverse-geocodes the fix to a place name.
  const detectLocation = useCallback(async () => {
    setGeo({ status: 'locating' });
    try {
      const position = await getCurrentPosition();
      const address = await reverseGeocode({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      const placeName =
        address && (address.city || address.town || address.state_district || address.county || address.village);
      setGeo({ status: 'done', address, placeName: placeName || null });
    } catch (err) {
      setGeo({ status: err && err.code === 1 ? 'denied' : 'error' });
    }
  }, []);

  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  const isLoading = locations === null;
  const locationList = locations || [];
  const nearbyLocation = geo.status === 'done' ? findLocationForAddress(locationList, geo.address) : null;
  // Put the detected city first so it's the obvious pick.
  const orderedLocations = nearbyLocation
    ? [nearbyLocation, ...locationList.filter((location) => location !== nearbyLocation)]
    : locationList;

  let geoMessage;
  if (geo.status === 'locating') {
    geoMessage = 'Finding your location…';
  } else if (geo.status === 'denied') {
    geoMessage = 'Location permission was denied. Allow it to find cities near you.';
  } else if (geo.status === 'error') {
    geoMessage = "We couldn't get your location.";
  } else if (nearbyLocation) {
    geoMessage = `You're in ${nearbyLocation.city}.`;
  } else if (isLoading) {
    geoMessage = geo.placeName ? `You're in ${geo.placeName}.` : 'Location found.';
  } else {
    geoMessage = geo.placeName
      ? `You're in ${geo.placeName}. No journey is live there yet — pick a city below.`
      : 'No journey is live near you yet — pick a city below.';
  }

  return (
    <div className="citypicker-screen">
      <div className="citypicker-frame">
        <div className="citypicker-content">
          <h1 className="citypicker-title">Choose your city</h1>
          <p className="citypicker-subtitle">We&apos;ll show you the journey that&apos;s live there.</p>

          <div className="citypicker-geo">
            <span className="citypicker-geo-icon">
              <LocateIcon />
            </span>
            <span className="citypicker-geo-text">{geoMessage}</span>
            {(geo.status === 'denied' || geo.status === 'error') && (
              <button type="button" className="citypicker-geo-retry" onClick={detectLocation}>
                Try again
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="citypicker-status">Loading cities&hellip;</div>
          ) : locationList.length === 0 ? (
            <div className="citypicker-status">No cities available yet. Check back soon!</div>
          ) : (
            <div className="citypicker-list">
              {orderedLocations.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  className={'citypicker-item' + (location === nearbyLocation ? ' citypicker-item-nearby' : '')}
                  onClick={() => onSelectLocation?.(location.id)}
                >
                  <span className="citypicker-item-icon">
                    <PinIcon />
                  </span>
                  <span className="citypicker-item-text">
                    <span className="citypicker-item-city">{location.city}</span>
                    <span className="citypicker-item-region">{location.state}, {location.country}</span>
                  </span>
                  {location === nearbyLocation && <span className="citypicker-item-badge">Near you</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
